import { ChevronRightIcon, Loader2Icon } from 'lucide-react';

import { OrderLineStatus } from '@invenco/common-interface/sales';
import type { SkuDTO } from '@invenco/products-interface';

import { Button } from '@/components/core/button';
import { DialogTrigger } from '@/components/core/dialog';
import { DialogWithContext } from '@/components/dialog-with-context';
import { StatusBadge } from '@/components/status-badge';
import { useGateways } from '@/gateways/GatewayProvider';
import { formatNumber } from '@/shared/helpers';
import type { Result } from '@/shared/helpers/Result';
import { useQueryWithInput } from '@/shared/hooks/queries';
import { orderLineStatusType } from '@/shared/status-maps';
import { orderLineStatusTitle } from '@/shared/title-maps';

import { AssignSkuDialogContent } from './assign-sku-dialog-content';
import type { OrderLineRow } from './order-lines-card-columns';

type OrderLineExpandedDetailsProps = {
  line: OrderLineRow;
  channelId?: string;
  editing?: boolean;
  onAssignSku?: (line: OrderLineRow, sku: SkuDTO) => Promise<void | Result<unknown>>;
};

function QuantitySeparator() {
  return <ChevronRightIcon className="size-3 text-muted-foreground" aria-hidden />;
}

function QuantityValue({ children }: { children: string }) {
  return <span className="text-foreground">{children}</span>;
}

export function OrderLineExpandedDetails({
  line,
  channelId,
  editing = false,
  onAssignSku,
}: OrderLineExpandedDetailsProps) {
  const { salesGateway } = useGateways();
  const status = line.status ?? OrderLineStatus.PENDING;
  const skuId = line.skuId;
  const { data: availableQuantity, isLoading } = useQueryWithInput({
    parentKey: 'order-line-card-listings',
    input: { skuIds: skuId ? [skuId] : [], channelIds: channelId ? [channelId] : [] },
    query: (input, { signal }) => salesGateway.getListings(input, { signal }),
    select: ({ items }) => items.reduce((sum, { qtyAvailable }) => sum + qtyAvailable, 0),
    enabled: editing && !!skuId && !!channelId,
  });

  const reservedQuantity = (
    <span>
      Reserved: <QuantityValue>{formatNumber(line.qtyReserved ?? 0)}</QuantityValue>
    </span>
  );
  const canAssignSku = editing && status === OrderLineStatus.ITEM_NOT_FOUND;

  return (
    <div className="space-y-2 text-xs text-muted-foreground sm:flex sm:items-center sm:justify-between sm:gap-4 sm:space-y-0">
      <div className="flex flex-wrap items-center gap-2">
        <span>Status</span>
        <QuantitySeparator />
        <StatusBadge
          status={status}
          type={orderLineStatusType}
          title={orderLineStatusTitle}
          size="sm"
        />
        {canAssignSku && onAssignSku ? (
          <DialogWithContext>
            <DialogTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={(event) => event.stopPropagation()}
                className="text-foreground"
              >
                Assign SKU
              </Button>
            </DialogTrigger>
            <AssignSkuDialogContent key={line.id} line={line} onAssignSku={onAssignSku} />
          </DialogWithContext>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {reservedQuantity}
        <QuantitySeparator />
        {editing ? (
          <>
            <span>
              Available:{' '}
              {isLoading && skuId && channelId ? (
                <Loader2Icon
                  className="inline size-3 animate-spin"
                  aria-label="Loading available quantity"
                />
              ) : (
                <QuantityValue>{formatNumber(availableQuantity ?? 0)}</QuantityValue>
              )}
            </span>
            {line.qtyShipped ? (
              <>
                <QuantitySeparator />
                <span>
                  Shipped: <QuantityValue>{formatNumber(line.qtyShipped)}</QuantityValue>
                </span>
              </>
            ) : null}
          </>
        ) : (
          <>
            <span>
              Committed: <QuantityValue>{formatNumber(line.qtyCommitted ?? 0)}</QuantityValue>
            </span>
            <QuantitySeparator />
            <span>
              Shipped: <QuantityValue>{formatNumber(line.qtyShipped ?? 0)}</QuantityValue>
            </span>
            <QuantitySeparator />
            <span>
              Balance: <QuantityValue>{formatNumber(line.qtyBalance ?? 0)}</QuantityValue>
            </span>
          </>
        )}
      </div>
    </div>
  );
}
